import type { ToolFamily } from "../core/tool-families";
import { defineComponent } from "vue";
import Icon from "@/components/ui/icon/Icon";
import McpIcon from "@/icons/svgs/MCP";
import { $t } from "@/services/i18n";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

const base = "inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-px text-2xs";

/**
 * AI-1195: origin seal for agent-tool cards and MCP list groups. MCP carries
 * the server name + host in indigo; API shows the host when available;
 * knowledge shows the collection/file name; manual functions show "Manual".
 */
export default defineComponent({
  name: "ToolOriginBadge",
  props: {
    family: p.$object<ToolFamily>(),
  },
  setup(props) {
    return () => {
      const family = props.family;
      if (family.kind !== "agent_tool") return null;

      if (family.agentType === "mcp") {
        return (
          <span class={cn(base, "border-indigo-6 bg-indigo-3 text-indigo-11")}>
            <McpIcon class="h-3 w-3 shrink-0" />
            <b class="font-semibold">{family.mcp.serverName}</b>
            {family.mcp.serverHost && (
              <span class="hidden font-mono opacity-75 sm:inline">{family.mcp.serverHost}</span>
            )}
          </span>
        );
      }

      if (family.agentType === "api") {
        return (
          <span data-accent="gray" class={cn(base, "border-accent-6 bg-accent-2 text-muted-foreground")}>
            <b class="font-semibold text-foreground">API</b>
            {family.api.host && <span class="hidden font-mono sm:inline">{family.api.host}</span>}
          </span>
        );
      }

      if (family.agentType === "knowledge" || family.agentType === "rag") {
        const collectionName = family.agentType === "rag" ? family.rag.collectionName : family.knowledge.collectionName;
        return (
          <span data-accent="gray" class={cn(base, "border-accent-6 bg-accent-2 text-muted-foreground")}>
            <Icon icon="lucide:database" class="h-3 w-3 shrink-0" />
            <span class="max-w-40 truncate">{collectionName}</span>
          </span>
        );
      }

      return (
        <span data-accent="gray" class={cn(base, "border-accent-6 bg-accent-2 text-muted-foreground")}>
          <b class="font-semibold text-foreground">{$t("agents:tool_families.manual")}</b>
        </span>
      );
    };
  },
});
